import { useEffect, useState } from "react";
import { Navigate, useNavigate, useLocation, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useTheme } from "@/context/ThemeContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Sparkles, Loader2, Moon, Sun, Eye, EyeOff, Wrench, ArrowLeft } from "lucide-react";

const LIGHT_BG = "https://static.prod-images.emergentagent.com/jobs/2f03a63b-8413-4f38-a52e-d763218b6d6f/images/101c818f2afba54eb20c0f53408babeae95f2e4dec85a4787852bb9222741967.png";
const DARK_BG = "https://static.prod-images.emergentagent.com/jobs/2f03a63b-8413-4f38-a52e-d763218b6d6f/images/ebdd3c5e6adad84bb18de6dbf883972461a1fc48eeb3c330cc11775c2fdf05fb.png";

export default function Login() {
  const { user, login } = useAuth();
  const { theme, toggle } = useTheme();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPwd, setShowPwd] = useState(false);
  const [loading, setLoading] = useState(false);
  const [maintenanceBlock, setMaintenanceBlock] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => { setEmail(""); setPassword(""); }, []);

  if (user && typeof user === "object") {
    const from = location.state?.from?.pathname || "/";
    return <Navigate to={from} replace />;
  }

  const onSubmit = async (e) => {
    e.preventDefault();
    setMaintenanceBlock(null);
    if (!email || !password) {
      toast.error("Inserisci email e password");
      return;
    }
    setLoading(true);
    const res = await login(email, password);
    setLoading(false);
    if (res.ok) {
      toast.success(`Bentornato, ${res.user.nome}`);
      navigate("/");
    } else if (res.maintenance) {
      setMaintenanceBlock({ error: res.error, message: res.message });
      toast.error(res.error);
    } else {
      toast.error(res.error || "Login fallito");
    }
  };

  const bg = theme === "dark" ? DARK_BG : LIGHT_BG;

  return (
    <div className="relative min-h-screen w-full overflow-hidden" data-testid="login-page">
      {/* Background */}
      <img src={bg} alt="" className="absolute inset-0 h-full w-full object-cover" />
      <div className={`absolute inset-0 ${theme === "dark" ? "bg-black/45" : "bg-white/35"}`} />
      <div className="absolute inset-0 ih-bg-ambient opacity-40" />

      {/* Theme toggle floating */}
      <button
        onClick={toggle}
        data-testid="login-theme-toggle"
        className="absolute top-6 right-6 z-10 h-10 w-10 rounded-xl ih-glass flex items-center justify-center hover:scale-105 transition-transform"
        aria-label="Toggle theme"
      >
        {theme === "dark" ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
      </button>

      <div className="relative z-10 min-h-screen flex items-center justify-center p-6">
        <div className="w-full max-w-[440px] ih-anim-in">
          <div className="ih-glass-strong rounded-3xl p-10">
            {/* Logo */}
            <div className="flex flex-col items-center gap-3 mb-10" data-testid="login-logo">
              <img
                src="/icon-192.png?v=107"
                alt="IH AuditFlow"
                className="app-logo w-16 h-16 object-contain rounded-2xl shadow-sm"
              />
              <span className="font-display font-semibold text-2xl tracking-tight text-foreground">
                IH AuditFlow
              </span>
            </div>

            <h1 className="font-display text-3xl sm:text-4xl tracking-tight font-light mb-2">
              Bentornato.
            </h1>
            <p className="text-sm text-muted-foreground mb-8">
              Accedi al pannello di controllo qualità e operations.
            </p>

            <form onSubmit={onSubmit} className="space-y-5" data-testid="login-form">
              {maintenanceBlock && (
                <div
                  className="rounded-xl border border-amber-300/60 bg-amber-100/70 dark:bg-amber-900/40 dark:border-amber-700/60 px-4 py-3 flex items-start gap-3"
                  data-testid="login-maintenance-banner"
                >
                  <Wrench className="h-4 w-4 mt-0.5 text-amber-700 dark:text-amber-300 shrink-0" />
                  <div className="text-left">
                    <div className="text-sm font-semibold text-amber-900 dark:text-amber-100">
                      {maintenanceBlock.error}
                    </div>
                    {maintenanceBlock.message && (
                      <div className="text-xs text-amber-800/90 dark:text-amber-200/80 mt-0.5">
                        {maintenanceBlock.message}
                      </div>
                    )}
                  </div>
                </div>
              )}
              <div className="space-y-2">
                <Label htmlFor="email" className="text-xs uppercase tracking-[0.14em] text-muted-foreground">Email</Label>
                <Input
                  id="email"
                  type="email"
                  data-testid="login-email-input"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="nome@investhospitality.it"
                  className="h-11 bg-background/60 border-border/60"
                  autoComplete="email"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="password" className="text-xs uppercase tracking-[0.14em] text-muted-foreground">Password</Label>
                <div className="relative">
                  <Input
                    id="password"
                    data-testid="login-password-input"
                    type={showPwd ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••"
                    className="h-11 bg-background/60 border-border/60 pr-10"
                    autoComplete="current-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPwd((v) => !v)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                    aria-label="Mostra password"
                  >
                    {showPwd ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>

              <Button
                type="submit"
                data-testid="login-submit-button"
                disabled={loading}
                className="w-full h-11 rounded-xl bg-primary text-primary-foreground hover:bg-primary/90 text-sm font-semibold tracking-wide"
              >
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Accedi"}
              </Button>
            </form>

            <div className="mt-4 text-center">
              <Link
                to="/maintenance"
                data-testid="login-back-to-maintenance"
                className="inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors"
              >
                <ArrowLeft className="h-3 w-3" />
                Torna alla pagina di manutenzione
              </Link>
            </div>

            <div className="mt-8 pt-6 border-t border-border/50 text-[11px] text-muted-foreground leading-relaxed">
              Piattaforma riservata al personale Invest Hospitality. Accessi monitorati e ispezione log attivo.
            </div>
            <div className="mt-4 text-center text-[10px] tracking-wide text-muted-foreground/60" data-testid="ptk-credit-login">
              Dev by PTK Solutions ® 2026
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
